import { useEffect, useState } from 'react';
import { AlertTriangle, Lock, CheckCircle2 } from 'lucide-react';

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

const ForgotLink = ({ onForgot, resetSent }) => (resetSent ? (
  <p className="mt-2 flex items-start gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400" data-testid="login-reset-sent">
    <CheckCircle2 className="h-3.5 w-3.5 mt-px shrink-0" aria-hidden="true" />
    Request sent. Your pharmacy owner will set a new password and share it with you.
  </p>
) : (
  <button type="button" onClick={onForgot} data-testid="login-forgot-password-link"
    className="mt-2 text-xs font-semibold underline underline-offset-2 hover:opacity-80">
    Forgot password? Ask your owner to reset it
  </button>
));

export const LoginLockNotice = ({ attemptsLeft, lockedUntil, onUnlock, onForgot, resetSent }) => {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!lockedUntil) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [lockedUntil]);

  const secs = lockedUntil ? Math.max(Math.ceil((lockedUntil - Date.now()) / 1000), 0) : 0;

  useEffect(() => {
    if (lockedUntil && secs === 0) onUnlock();
  }, [lockedUntil, secs, onUnlock]);

  if (lockedUntil && secs > 0) {
    return (
      <div role="alert" data-testid="login-locked-notice"
        className="flex items-start gap-2.5 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
        <Lock className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
        <div>
          <p className="font-semibold">Too many wrong passwords</p>
          <p className="text-xs mt-0.5">
            Sign-in is locked for this account. Try again in{' '}
            <span className="font-mono font-semibold" data-testid="login-locked-countdown">{fmt(secs)}</span>
            {' '}or ask your owner to unlock you.
          </p>
          <button type="button" onClick={onUnlock} data-testid="login-locked-retry"
            className="mt-2 mr-3 text-xs font-semibold underline underline-offset-2 hover:opacity-80">
            Owner unlocked me — try again
          </button>
          <ForgotLink onForgot={onForgot} resetSent={resetSent} />
        </div>
      </div>
    );
  }

  if (attemptsLeft == null) return null;
  return (
    <div role="alert" data-testid="login-attempts-notice"
      className="flex items-start gap-2.5 rounded-lg border border-amber-400/50 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
      <div>
        <p>
          Wrong email or password.{' '}
          <span className="font-semibold" data-testid="login-attempts-left">
            {attemptsLeft} {attemptsLeft === 1 ? 'try' : 'tries'} left
          </span>{' '}
          before sign-in is locked for 15 minutes.
        </p>
        <ForgotLink onForgot={onForgot} resetSent={resetSent} />
      </div>
    </div>
  );
};
